import { alert, validate } from 'utilities'

import Button from './button'
import FileUploadField from './file-upload-field'
import InputGroup from './input-group'
import Modal from './modal'
import ModalCloseBtn from './modal-close-btn'
import PropTypes from 'prop-types'
import StatusMessage from './status-message'
import { useCreateMediaMutation } from 'services/media.service'
import { useHandleApiResponse } from 'hooks'
import { useState } from 'react'

const UploadMediaModal = ({ isOpen, close }) => {
  const [title, setTitle] = useState('')
  const [type, setType] = useState('image')
  const [image, setImage] = useState(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [errors, setErrors] = useState({})

  const [createMedia, { data: createMediaResponse, isLoading: isCreatingMedia, ...response }] = useCreateMediaMutation()

  const clearForm = () => {
    setTitle('')
    setType('image')
    setImage(null)
    setVideoUrl('')
    setErrors({})
  }

  const handleCreateMediaSuccess = () => {
    alert.success('Media uploaded successfully.')
    clearForm()
    close()
  }

  useHandleApiResponse({
    data: createMediaResponse,
    handleSuccess: handleCreateMediaSuccess,
    ...response,
  })

  const handleSetImage = (file) => {
    setErrors((prev) => ({ ...prev, image: '' }))
    setImage(file)
  }

  const handleTitleChange = (value) => {
    setTitle(value)
    setErrors((prev) => ({ ...prev, title: '' }))
  }

  const handleVideoUrlChange = (value) => {
    setVideoUrl(value)
    setErrors((prev) => ({ ...prev, videoUrl: '' }))
  }

  const handleTypeChange = (newType) => {
    setType(newType)
    setErrors({})
    // Reset type-specific fields
    if (newType === 'image') {
      setVideoUrl('')
    } else {
      setImage(null)
    }
  }

  const handleSubmit = (ev) => {
    ev.preventDefault()

    const { errors, hasErrors } = validate.createMedia({ title, image, type, videoUrl })

    if (hasErrors) {
      setErrors(errors)
    } else {
      if (type === 'video') {
        // Send as JSON for video type
        createMedia({ title, type: 'video', video_url: videoUrl })
      } else {
        // Send as FormData for image upload
        const formData = new FormData()
        formData.append('title', title)
        formData.append('image', image)
        formData.append('type', 'image')
        createMedia(formData)
      }
    }
  }

  return (
    <Modal isOpen={isOpen} close={close} dialogClassName={'!w-[480px]'}>
      <div>
        <ModalCloseBtn close={close} />

        <h3 className='text-base font-semibold leading-[28px] mb-3 text-[#222]'>Upload new media</h3>

        <form onSubmit={handleSubmit}>
          {/* Media Type Toggle */}
          <div className='mb-4'>
            <label className='form-label'>Media Type</label>
            <div className='flex gap-2 mt-1'>
              <button
                type='button'
                onClick={() => handleTypeChange('image')}
                className={`px-4 py-2 text-xs font-medium rounded border border-solid transition-colors ${
                  type === 'image' ? 'bg-[#222] text-white border-[#222]' : 'bg-white text-[#333] border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                Image
              </button>
              <button
                type='button'
                onClick={() => handleTypeChange('video')}
                className={`px-4 py-2 text-xs font-medium rounded border border-solid transition-colors ${
                  type === 'video' ? 'bg-[#222] text-white border-[#222]' : 'bg-white text-[#333] border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                Video Link
              </button>
            </div>
          </div>

          <InputGroup
            label={'Title'}
            inputProps={{ placeholder: 'Enter media title', value: title, onChange: handleTitleChange }}
            className={'mb-4'}
            statusMessageProps={{ status: 'error', message: errors?.title }}
          />

          {type === 'image' ? (
            <>
              <label className='form-label' htmlFor='media'>
                Upload Media{' '}
              </label>
              <FileUploadField
                acceptableFiles={ACCEPTABLE_LETTER_FILES}
                invalidFileMessage='Please upload only valid image files.'
                handleFileUpload={handleSetImage}
              />
              <StatusMessage status='error' message={errors?.image ?? ''} />
            </>
          ) : (
            <InputGroup
              label={'Video URL'}
              inputProps={{
                placeholder: 'Paste YouTube, Vimeo, or other video link',
                value: videoUrl,
                onChange: handleVideoUrlChange,
              }}
              className={'mb-4'}
              extraLabelInfo={'YouTube, Vimeo, Dailymotion, etc.'}
              statusMessageProps={{ status: 'error', message: errors?.videoUrl }}
            />
          )}

          <div className='flex flex-col-reverse xxs:flex-row items-center justify-end gap-x-4 mt-8 gap-y-2'>
            <Button variant='outlined' onClick={close} disabled={isCreatingMedia} className={'w-full xxs:w-max'}>
              Cancel
            </Button>

            <Button type='submit' isLoading={isCreatingMedia} className={'w-full xxs:w-max'}>
              {type === 'video' ? 'Add Video' : 'Upload'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default UploadMediaModal

UploadMediaModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
}

export const ACCEPTABLE_LETTER_FILES = ['image/png', 'image/jpeg']
